<template>
  <div>
    <v-dialog
      v-model="visible"
      @keydown.esc="close"
      persistent
      scrollable
      max-width="1200"
      style="z-index: 6"
    >
    <v-form ref="addItemForm" @submit.prevent="submit">
      <v-card class="glass-card-dialog">
        <!-- Glass Header with Gradient -->
        <div class="glass-header-dialog">
          <v-row class="align-center ma-0 pa-0">
            <v-col cols="auto">
              <h2 class="gradient-text-dialog">
                {{ isEditMode ? 'Uredi Artikl' : 'Novi Artikl' }}
              </h2>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <v-btn icon @click="close" class="close-btn-dialog">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <!-- Tabs for Organization -->
        <v-tabs v-model="activeTab" class="dialog-tabs" centered grow>
          <v-tab class="dialog-tab">
            <v-icon left small>mdi-information</v-icon>
            Osnovni Podaci
            <v-icon v-if="tabErrors.osnovni" small class="error-indicator ml-2">mdi-alert-circle</v-icon>
          </v-tab>
          <v-tab v-if="warehouseModule" class="dialog-tab">
            <v-icon left small>mdi-ruler</v-icon>
            Jedinice Mjere
            <v-icon v-if="tabErrors.jedinice" small class="error-indicator ml-2">mdi-alert-circle</v-icon>
          </v-tab>
          <v-tab v-if="warehouseModule" class="dialog-tab">
            <v-icon left small>mdi-file-document</v-icon>
            Normativ
            <v-icon v-if="tabErrors.normativ" small class="error-indicator ml-2">mdi-alert-circle</v-icon>
          </v-tab>
          <v-tab class="dialog-tab">
            <v-icon left small>mdi-cash-multiple</v-icon>
            Naknade
          </v-tab>
          <v-tab v-if="warehouseModule" class="dialog-tab">
            <v-icon left small>mdi-truck</v-icon>
            Dobavljači
            <v-icon v-if="tabErrors.dobavljaci" small class="error-indicator ml-2">mdi-alert-circle</v-icon>
          </v-tab>
          <v-tab class="dialog-tab">
            <v-icon left small>mdi-image</v-icon>
            Slika
            <v-icon v-if="tabErrors.slika" small class="error-indicator ml-2">mdi-alert-circle</v-icon>
          </v-tab>
          <v-tab class="dialog-tab">
            <v-icon left small>mdi-credit-card-plus</v-icon>
            Cjenik
            <v-icon v-if="tabErrors.cjenik" small class="error-indicator ml-2">mdi-alert-circle</v-icon>
          </v-tab>
        </v-tabs>

        <v-card-text class="dialog-content pa-6">
          <v-tabs-items v-model="activeTab">

            <!-- TAB 1: OSNOVNI PODACI -->
            <v-tab-item>
              <v-container fluid class="pa-4">
                <!-- Naziv artikla - Full Width -->
                <v-row dense class="mb-2">
                  <v-col cols="12">
                    <v-textarea
                      rows="2"
                      dense
                      outlined
                      label="Naziv artikla"
                      placeholder="Unesite naziv artikla..."
                      v-model="name"
                      :rules="[rules.req]"
                      :error="nameError"
                      autofocus
                      hide-details
                      class="filter-select"
                    ></v-textarea>
                  </v-col>
                </v-row>

                <!-- Row 1: Barkod / Vrsta artikla -->
                <v-row dense class="mb-2">
                  <v-col cols="12" md="6">
                    <v-text-field
                      dense
                      outlined
                      label="Barkod"
                      placeholder="1234567890"
                      v-model="barcode"
                      hide-details
                      class="filter-select"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      dense
                      outlined
                      label="Vrsta artikla"
                      v-model="type"
                      item-text="name"
                      return-object
                      :items="itemTypes"
                      :rules="[rules.req]"
                      :error="typeError"
                      hide-details
                      class="filter-select"
                    ></v-select>
                  </v-col>
                </v-row>

                <!-- Row 2: Šifra / KPD Šifra -->
                <v-row dense class="mb-2">
                  <v-col cols="12" md="6">
                    <v-text-field
                      dense
                      outlined
                      label="Šifra"
                      placeholder="ART-001"
                      v-model="code"
                      @input="checkCodeAvailability"
                      validate-on-blur
                      :rules="[rules.codeused]"
                      :error="codeError"
                      class="filter-select"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      dense
                      outlined
                      label="KPD Šifra"
                      placeholder="KPD-001"
                      v-model="kpdcode"
                      hide-details
                      class="filter-select"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Row 3: Porezna grupa / Prodaje se (samo za warehouse) -->
                <v-row dense class="mb-2" v-if="warehouseModule">
                  <v-col cols="12" md="6">
                    <v-select
                      dense
                      outlined
                      label="Porezna grupa"
                      v-model="selectedTaxGroup"
                      item-text="text"
                      item-value="value"
                      :items="taxGroups"
                      :rules="[rules.req]"
                      :error="taxGroupError"
                      hide-details
                      class="filter-select"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6" class="d-flex align-center">
                    <v-checkbox
                      v-model="whSellingItem"
                      label="Prodaje se"
                      dense
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                  </v-col>
                </v-row>

                <!-- Row 4: Osnovna jedinica / Jedinica za prodaju -->
                <v-row dense class="mb-2" v-if="warehouseModule">
                  <v-col cols="12" md="6">
                    <v-text-field
                      outlined
                      dense
                      v-model="whStorageUnit"
                      label="Osnovna jedinica"
                      :rules="[rules.req]"
                      :error="whStorageUnitError"
                      hide-details
                      class="filter-select"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      outlined
                      dense
                      v-model="sellingUnit"
                      label="Jedinica za prodaju"
                      hide-details
                      class="filter-select"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Non-warehouse module fields -->
                <v-row dense class="mb-2" v-if="!warehouseModule">
                  <v-col cols="12" md="6">
                    <v-select
                      dense
                      outlined
                      label="Skladišna jedinica"
                      v-model="storageUnit"
                      item-text="name"
                      return-object
                      :items="storageUnits"
                      :rules="[rules.req]"
                      :error="storageUnitError"
                      hide-details
                      class="filter-select"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      dense
                      outlined
                      label="Prodajna jedinica"
                      v-model="sellingUnit"
                      item-text="name"
                      return-object
                      :items="sellingUnits"
                      :rules="[rules.req]"
                      :error="sellingUnitError"
                      hide-details
                      class="filter-select"
                    ></v-select>
                  </v-col>
                </v-row>

                <v-row v-if="warehouseModule" class="align-center">
                  <!-- Hidden checkboxes - always checked by default -->
                  <v-col cols="12" sm="4" v-show="false">
                    <v-checkbox
                      v-model="whDefaultSale"
                      @change="changeDefaultSale(whDefaultSale)"
                      label="Zadana prodajna jedinica"
                      dense
                      hide-details
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" sm="4" v-show="false">
                    <v-checkbox
                      v-model="whDefaultStore"
                      @change="changeDefaultStore(whDefaultStore)"
                      label="Zadana skladišna jedinica"
                      dense
                      hide-details
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- TAB 2: JEDINICE MJERE -->
            <v-tab-item v-if="warehouseModule">
              <v-container fluid class="pa-0">
                <v-row>
                  <v-col cols="12">
                    <div class="section-header mb-4">
                      <h3 class="section-title">Dodatne Jedinice Mjere</h3>
                      <p class="section-subtitle">Definirajte dodatne jedinice za pakiranje i prodaju</p>
                    </div>
                  </v-col>
                </v-row>

                <v-row v-if="newUnits.length === 0">
                  <v-col cols="12" class="text-center py-8">
                    <v-icon size="64" color="grey lighten-1">mdi-ruler-square</v-icon>
                    <p class="grey--text mt-3">Nema definiranih dodatnih jedinica</p>
                    <v-btn class="soft-button mt-3" @click="addMoreUnits">
                      <v-icon left>mdi-plus</v-icon>
                      Dodaj Jedinicu
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row v-else>
                  <v-col cols="12">
                    <v-card
                      v-for="(unit, idx) in newUnits"
                      :key="idx"
                      class="mb-3 unit-card"
                      outlined
                    >
                      <v-card-text>
                        <v-row>
                          <v-col cols="12" sm="3">
                            <v-text-field
                              outlined
                              dense
                              v-model="unit.customItemUnit"
                              label="Jedinica mjere"
                              class="modern-input"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" sm="3">
                            <quantity-input
                              outlined
                              dense
                              label="Količina"
                              v-model="unit.customItemAmount"
                            ></quantity-input>
                          </v-col>
                          <v-col cols="12" sm="2">
                            <v-text-field
                              outlined
                              dense
                              :value="whStorageUnit"
                              disabled
                              label="Osnovna jed."
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" sm="4">
                            <v-checkbox
                              v-model="unit.sellingItem"
                              label="Prodajna jedinica"
                              dense
                              hide-details
                              class="mb-1"
                            ></v-checkbox>
                            <v-checkbox
                              v-model="unit.defaultSale"
                              @change="changeDefaultSaleCustom(unit.defaultSale, idx)"
                              label="Zadana prodajna"
                              dense
                              hide-details
                              class="mb-1"
                            ></v-checkbox>
                            <v-checkbox
                              v-model="unit.defaultStore"
                              @change="changeDefaultStoreCustom(unit.defaultStore, idx)"
                              label="Zadana skladišna"
                              dense
                              hide-details
                            ></v-checkbox>
                          </v-col>
                          <v-col cols="12" sm="1" class="d-flex align-center justify-center">
                            <v-btn icon color="error" @click="removeUnit(idx)">
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                    <v-btn class="soft-button mt-3" @click="addMoreUnits" small>
                      <v-icon left small>mdi-plus</v-icon>
                      Dodaj Jedinicu
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- TAB 3: NORMATIV -->
            <v-tab-item v-if="warehouseModule">
              <v-container fluid class="pa-0">
                <v-row>
                  <v-col cols="12">
                    <div class="section-header mb-4">
                      <h3 class="section-title">Normativ (Recept)</h3>
                      <p class="section-subtitle">Definirajte sastojke za složene artikle</p>
                    </div>
                  </v-col>
                </v-row>

                <v-row v-if="norms.length === 0">
                  <v-col cols="12" class="text-center py-8">
                    <v-icon size="64" color="grey lighten-1">mdi-file-document-outline</v-icon>
                    <p class="grey--text mt-3">Nema definiranog normativa</p>
                    <v-btn class="soft-button mt-3" @click="openAddNormForm">
                      <v-icon left>mdi-plus</v-icon>
                      Dodaj Normativ
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row v-else>
                  <v-col cols="12">
                    <div class="norms-table-wrapper">
                      <v-data-table
                        :headers="[
                          { text: 'Artikl', value: 'name', align: 'start' },
                          { text: 'Količina', value: 'quantity', align: 'end' }
                        ]"
                        :items="tableRows"
                        :items-per-page="-1"
                        hide-default-footer
                        class="norms-table"
                      >
                        <template v-slot:item.name="{ item }">
                          <div class="item-name-cell">
                            <v-icon color="#3989C9" small class="mr-2">mdi-food</v-icon>
                            <span>{{ item.name }}</span>
                          </div>
                        </template>
                        <template v-slot:item.quantity="{ item }">
                          <div class="quantity-cell">
                            <v-chip small class="quantity-chip">
                              {{ item.quantity }}
                            </v-chip>
                          </div>
                        </template>
                      </v-data-table>
                    </div>
                    <v-btn class="soft-button mt-4" @click="openAddNormForm" small>
                      <v-icon left small>mdi-pencil</v-icon>
                      Uredi Normativ
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- TAB 4: NAKNADE -->
            <v-tab-item>
              <v-container fluid class="pa-0">
                <v-row>
                  <v-col cols="12">
                    <div class="section-header mb-4">
                      <h3 class="section-title">Naknade</h3>
                      <p class="section-subtitle">Povratne naknade i ostali troškovi</p>
                    </div>
                  </v-col>
                </v-row>

                <v-row v-if="naknade.length === 0">
                  <v-col cols="12" class="text-center py-8">
                    <v-icon size="64" color="grey lighten-1">mdi-cash-multiple</v-icon>
                    <p class="grey--text mt-3">Nema definiranih naknada</p>
                    <v-btn class="soft-button mt-3" @click="addNaknada">
                      <v-icon left>mdi-plus</v-icon>
                      Dodaj Naknadu
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row v-else>
                  <v-col cols="12">
                    <v-card
                      v-for="(naknada, idx) in naknade"
                      :key="idx"
                      class="mb-3 naknade-card"
                      outlined
                    >
                      <v-card-text>
                        <v-row>
                          <v-col cols="12" sm="6" md="2">
                            <v-select
                              dense
                              outlined
                              label="Vrsta naknade"
                              v-model="naknada.type"
                              :items="naknadeTypes"
                              item-text="label"
                              item-value="value"
                              hide-details
                              class="modern-input"
                            ></v-select>
                          </v-col>
                          <v-col cols="12" sm="6" md="2">
                            <v-text-field
                              dense
                              outlined
                              type="number"
                              label="Iznos (EUR)"
                              v-model.number="naknada.amount"
                              hide-details
                              class="modern-input"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" sm="6" md="2">
                            <v-text-field
                              dense
                              outlined
                              type="number"
                              label="Osnovica"
                              v-model.number="naknada.base"
                              hide-details
                              class="modern-input"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" sm="6" md="2">
                            <v-select
                              dense
                              outlined
                              label="Porezna stopa"
                              v-model="naknada.taxRate"
                              :items="taxRates"
                              item-text="label"
                              item-value="value"
                              hide-details
                              class="modern-input"
                            ></v-select>
                          </v-col>
                          <v-col cols="12" sm="6" md="3" class="d-flex align-center">
                            <v-checkbox
                              v-model="naknada.sellable"
                              label="Prodaje se"
                              dense
                              hide-details
                              class="mt-0"
                            ></v-checkbox>
                            <v-icon
                              small
                              color="#3989C9"
                              @click="showSellableHelp = true"
                              class="ml-1 help-icon-clickable"
                            >
                              mdi-help-circle-outline
                            </v-icon>
                          </v-col>
                          <v-col cols="12" sm="12" md="1" class="d-flex align-center justify-center">
                            <v-btn icon color="error" @click="removeNaknada(idx)">
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                    <v-btn class="soft-button mt-2" @click="addNaknada" small>
                      <v-icon left small>mdi-plus</v-icon>
                      Dodaj Naknadu
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- TAB 5: DOBAVLJAČI -->
            <v-tab-item v-if="warehouseModule">
              <v-container fluid class="pa-0">
                <v-row>
                  <v-col cols="12">
                    <div class="section-header mb-4">
                      <h3 class="section-title">Dobavljači</h3>
                      <p class="section-subtitle">Povežite artikl s dobavljačima</p>
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="8">
                    <v-select
                      dense
                      outlined
                      label="Odaberi dobavljača"
                      v-model="supplier"
                      item-text="name"
                      return-object
                      :items="suppliers"
                      class="modern-input"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-btn
                      class="soft-button"
                      style="width: 100%; height: 40px;"
                      @click="openSupplier()"
                      :disabled="supplier === undefined"
                    >
                      <v-icon left small>mdi-pencil</v-icon>
                      Uredi Šifre
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row v-if="itemSuppliers && itemSuppliers.length > 0">
                  <v-col cols="12">
                    <v-data-table
                      :headers="[
                        { text: 'Ime dobavljača', value: 'name' },
                        { text: 'Šifra kod dobavljača', value: 'item_code' },
                        { text: 'Ukloni', value: 'delete', sortable: false }
                      ]"
                      :items="itemSuppliers"
                      class="elevation-1"
                      :items-per-page="-1"
                      hide-default-footer
                    >
                      <template v-slot:item.item_code="{ item }">
                        <v-edit-dialog :return-value.sync="item.item_code">
                          {{ item.item_code }}
                          <template v-slot:input>
                            <v-text-field
                              v-model="item.item_code"
                              label="Uredi"
                              single-line
                            ></v-text-field>
                          </template>
                        </v-edit-dialog>
                      </template>
                      <template v-slot:item.delete="{ item, index }">
                        <v-btn icon small color="error" @click="removeSupplier(index)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>

                <v-row v-else>
                  <v-col cols="12" class="text-center py-8">
                    <v-icon size="64" color="grey lighten-1">mdi-truck-outline</v-icon>
                    <p class="grey--text mt-3">Nema dodanih dobavljača</p>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- TAB 6: SLIKA -->
            <v-tab-item>
              <v-container fluid class="pa-0">
                <v-row>
                  <v-col cols="12">
                    <div class="section-header mb-4">
                      <h3 class="section-title">Slika Artikla</h3>
                      <p class="section-subtitle">Dodajte fotografiju artikla</p>
                    </div>
                  </v-col>
                </v-row>

                <v-row class="justify-center">
                  <v-col cols="12" md="6" class="text-center">
                    <div class="image-preview-container mb-4">
                      <v-img
                        ref="itemImg"
                        :src="itemImg ? itemImg : require('@/assets/no-item-small.jpg')"
                        contain
                        height="240px"
                        width="240px"
                        class="image-preview mx-auto"
                      >
                        <template v-slot:placeholder>
                          <v-img
                            :src="require('@/assets/no-item-small.jpg')"
                          ></v-img>
                        </template>
                      </v-img>
                    </div>
                  </v-col>
                </v-row>

                <v-row class="justify-center">
                  <v-col cols="12" md="6">
                    <v-file-input
                      label="Odaberi sliku artikla (max 500KB)"
                      ref="imageUpl"
                      @change="onFileChange"
                      clearable
                      prepend-icon="mdi-camera"
                      outlined
                      dense
                      accept="image/*"
                      class="modern-input"
                      hint="Maksimalna veličina: 500KB"
                      persistent-hint
                    ></v-file-input>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <!-- TAB 7: CJENIK -->
            <v-tab-item>
              <v-container fluid class="pa-4">
                <v-row>
                  <v-col cols="12">
                    <div class="section-header mb-4">
                      <h3 class="section-title">Cjenik</h3>
                      <p class="section-subtitle">Dodajte artikl na cjenik (opcionalno)</p>
                    </div>
                  </v-col>
                </v-row>

                <!-- Location Selector - Only show if not in POS and multiple locations exist -->
                <v-row v-if="!isInPointOfSale && locations.length > 1">
                  <v-col cols="12">
                    <v-select
                      dense
                      outlined
                      label="Prodajno mjesto"
                      v-model="selectedPriceListLocation"
                      :items="locations"
                      item-text="name"
                      item-value="id"
                      @change="onPriceListLocationChange"
                      class="filter-select"
                      hide-details
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="#3989C9">mdi-store</v-icon>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>

                <v-row v-if="priceListLoading">
                  <v-col cols="12" class="text-center py-8">
                    <v-progress-circular indeterminate color="#3989C9" size="64"></v-progress-circular>
                    <p class="grey--text mt-3">Učitavanje cjenika...</p>
                  </v-col>
                </v-row>

                <price-list-fields
                  v-else-if="selectedPriceListLocation || isInPointOfSale"
                  v-model="priceListData"
                  :warehouse-module="warehouseModule"
                  :item-id="itemId"
                  :locations="locations"
                  :units="newUnits"
                  :validation-errors="cjenikValidationErrors"
                  :tax-group="selectedTaxGroup"
                />

                <v-row v-else>
                  <v-col cols="12" class="text-center py-8">
                    <v-icon size="64" color="grey lighten-1">mdi-store-alert</v-icon>
                    <p class="grey--text mt-3">Molimo odaberite prodajno mjesto za pregled cjenika</p>
                  </v-col>
                </v-row>

                <v-row v-if="priceListExists && !priceListLoading">
                  <v-col cols="12">
                    <v-alert dense outlined type="success" color="green" class="mt-4">
                      <v-icon left>mdi-check-circle</v-icon>
                      Ovaj artikl je već na cjeniku. Možete ažurirati podatke.
                    </v-alert>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

          </v-tabs-items>
        </v-card-text>

        <!-- Footer Actions -->
        <v-card-actions class="dialog-actions px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn outlined class="cancel-btn-dialog" @click="close" large>
            Odustani
          </v-btn>
          <v-btn
            class="submit-btn-dialog"
            :loading="submitting"
            :disabled="submitting || isExistingCode"
            type="submit"
            large
          >
            <v-icon left>mdi-content-save</v-icon>
            {{ isEditMode ? 'Spremi Promjene' : 'Spremi Artikl' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <!-- Child Dialogs -->
    <add-norm ref="addNorm" @add="onAddNorm"></add-norm>
    <add-unit ref="addUnit" @add="onAddUnit"></add-unit>
    <add-item-supplier-codes ref="addCodes" @add="onAddCodes"></add-item-supplier-codes>
  </v-dialog>

  <!-- Sellable Help Modal -->
  <v-dialog
    v-model="showSellableHelp"
    max-width="500"
    persistent
  >
    <v-card class="help-modal-card">
      <v-card-title class="help-modal-header">
        <v-icon left color="white">mdi-help-circle</v-icon>
        <span class="help-modal-title">Prodaje se - Objašnjenje</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="showSellableHelp = false" class="help-modal-close">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="help-modal-content pa-6">
        <div class="help-section mb-4">
          <div class="help-icon-wrapper mb-3">
            <v-icon size="48" color="#3989C9">mdi-checkbox-marked-circle</v-icon>
          </div>
          <h3 class="help-subtitle mb-2">Kada je "Prodaje se" odabrano:</h3>
          <p class="help-text">
            Proizvod se prodaje s povratnom ambalažom koja je zaračunata kupcu te se isti iznos fiskalizira.
            Naknada će biti uključena u račun i poslana na fiskalizaciju.
          </p>
        </div>

        <v-divider class="my-4"></v-divider>

        <div class="help-section">
          <div class="help-icon-wrapper mb-3">
            <v-icon size="48" color="#FF9800">mdi-checkbox-blank-circle-outline</v-icon>
          </div>
          <h3 class="help-subtitle mb-2">Kada "Prodaje se" nije odabrano:</h3>
          <p class="help-text">
            Povratna naknada se ne prodaje i neće biti fiskalizirana.
            Ovo se koristi u slučajevima poput kafića i restorana gdje se ambalaža ne naplaćuje kupcu.
          </p>
        </div>
      </v-card-text>

      <v-card-actions class="help-modal-actions pa-4">
        <v-spacer></v-spacer>
        <v-btn
          class="help-modal-btn"
          @click="showSellableHelp = false"
          large
        >
          <v-icon left>mdi-check</v-icon>
          Razumijem
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import QuantityInput from '../../../components/QuantityInput.vue'
import AddNorm from './AddNorm.vue'
import AddUnit from './AddUnit.vue'
import AddItemSupplierCodes from './AddItemSupplierCodes.vue'
import PriceListFields from './shared/PriceListFields.vue'
import itemDialogMixin from './mixins/itemDialogMixin'

export default {
  name: 'ItemDialog',

  components: {
    QuantityInput,
    AddNorm,
    AddUnit,
    AddItemSupplierCodes,
    PriceListFields
  },

  mixins: [itemDialogMixin],

  methods: {
    hasWarehouse () {
      const state = require('@/state').default
      return state.getCurrentCompany().warehouse === 'module'
    }
  }
}
</script>

<style scoped>
/* Sve stilove iz AddItem.vue prebaciti ovdje - već su definirani u mixinu */
.glass-card-dialog {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  overflow: hidden;
}

.glass-header-dialog {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%) !important;
  padding: 12px 16px 10px;
  position: relative;
}

.gradient-text-dialog {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: white !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.5px !important;
  margin: 0;
}

.warehouse-debug {
  font-size: 12px !important;
  font-weight: 400 !important;
  opacity: 0.8;
  font-style: italic;
}

.close-btn-dialog {
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
}

.close-btn-dialog:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  transform: rotate(90deg);
  transition: all 0.3s ease;
}

.dialog-tabs {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.05) 0%,
    rgba(57, 137, 201, 0.03) 50%,
    rgba(97, 84, 164, 0.05) 100%) !important;
  border-bottom: 2px solid rgba(0, 179, 190, 0.15);
}

.dialog-tab {
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  color: rgba(57, 137, 201, 0.8) !important;
  transition: all 0.3s ease !important;
}

.dialog-tab:hover {
  color: #3989C9 !important;
  background: rgba(57, 137, 201, 0.08) !important;
}

.v-tab--active.dialog-tab {
  color: #3989C9 !important;
  font-weight: 600 !important;
  background: rgba(57, 137, 201, 0.12) !important;
}

.v-tabs-slider {
  background-color: #3989C9 !important;
  height: 3px !important;
}

.dialog-content {
  height: 550px;
  overflow-y: auto;
}

.section-header {
  border-left: 4px solid #3989C9;
  padding-left: 16px;
}

.section-title {
  font-size: 20px !important;
  font-weight: 600 !important;
  color: #3989C9 !important;
  margin: 0;
  letter-spacing: 0.3px;
}

.section-subtitle {
  font-size: 13px !important;
  color: rgba(70, 70, 80, 0.7) !important;
  margin: 4px 0 0 0;
}

.modern-input.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
}

.modern-input.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.modern-input.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.modern-input >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px) !important;
}

/* Filter Select Inputs - Same as Lager.vue */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined >>> .v-input__control {
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined >>> .v-text-field__slot {
  border-radius: 8px !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #3989C9 !important;
}

/* Label stays blue in all states */
.filter-select.v-input--is-focused >>> label,
.filter-select.v-input--is-dirty >>> label,
.filter-select.v-input--has-state >>> label {
  color: #3989C9 !important;
  font-weight: 600 !important;
}

.filter-select >>> input::placeholder,
.filter-select >>> textarea::placeholder {
  color: #3989C9 !important;
  opacity: 0.7 !important;
}

/* Filter Select Dropdown Menu */
.filter-select >>> .v-menu__content {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 8px !important;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: 0 8px 24px rgba(0, 179, 190, 0.12) !important;
}

.filter-select >>> .v-list-item {
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all 0.2s ease !important;
}

.filter-select >>> .v-list-item:hover {
  background: rgba(57, 137, 201, 0.08) !important;
}

.filter-select >>> .v-list-item--active {
  background: rgba(0, 179, 190, 0.12) !important;
  color: #3989C9 !important;
}

.filter-select >>> .v-simple-checkbox {
  color: #3989C9 !important;
}

.image-preview-container {
  display: inline-block;
  border: 2px dashed rgba(57, 137, 201, 0.3);
  border-radius: 12px;
  padding: 8px;
  transition: all 0.3s ease;
}

.image-preview-container:hover {
  border-color: rgba(57, 137, 201, 0.6);
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.15);
}

.image-preview {
  border-radius: 8px;
  overflow: hidden;
}

.naknade-card {
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.naknade-card:hover {
  border-color: rgba(57, 137, 201, 0.4) !important;
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.15) !important;
}

.unit-card {
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.unit-card:hover {
  border-color: rgba(57, 137, 201, 0.4) !important;
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.15) !important;
}

.soft-button {
  background: rgba(255, 255, 255, 0.9) !important;
  color: #3989C9 !important;
  border-radius: 8px !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  transition: all 0.3s ease !important;
}

.soft-button:hover {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.4) !important;
  transform: translateY(-1px);
}

.dialog-actions {
  border-top: 1px solid rgba(0, 179, 190, 0.15);
  background: rgba(250, 250, 250, 0.5);
}

.cancel-btn-dialog {
  color: rgba(70, 70, 80, 0.8) !important;
  border-color: rgba(120, 120, 130, 0.3) !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0.3px !important;
  padding: 0 24px !important;
}

.cancel-btn-dialog:hover {
  background: rgba(120, 120, 130, 0.08) !important;
  border-color: rgba(120, 120, 130, 0.5) !important;
}

.submit-btn-dialog {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.9) 50%,
    rgba(97, 84, 164, 0.95) 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 179, 190, 0.3) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 15px !important;
  letter-spacing: 0.3px !important;
  padding: 0 32px !important;
  transition: all 0.3s ease !important;
}

.submit-btn-dialog:hover {
  box-shadow: 0 6px 16px rgba(0, 179, 190, 0.4) !important;
  transform: translateY(-2px) scale(1.02);
}

.submit-btn-dialog:disabled {
  opacity: 0.6 !important;
}

/* Norms Table Styling */
.norms-table-wrapper {
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 179, 190, 0.08) !important;
}

.norms-table {
  background: transparent !important;
}

.norms-table >>> .v-data-table__wrapper {
  background: transparent !important;
}

/* Table Header */
.norms-table >>> thead {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.12) 0%,
    rgba(57, 137, 201, 0.1) 50%,
    rgba(97, 84, 164, 0.12) 100%) !important;
}

.norms-table >>> thead th {
  background: transparent !important;
  color: #3989C9 !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  border-bottom: 2px solid rgba(0, 179, 190, 0.3) !important;
  padding: 16px 16px !important;
}

/* Table Rows */
.norms-table >>> tbody tr {
  transition: all 0.2s ease !important;
}

.norms-table >>> tbody tr:hover {
  background: rgba(0, 179, 190, 0.05) !important;
}

.norms-table >>> tbody tr:not(:last-child) td {
  border-bottom: 1px solid rgba(0, 179, 190, 0.1) !important;
}

.norms-table >>> tbody td {
  padding: 14px 16px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
}

/* Item Name Cell */
.item-name-cell {
  display: flex;
  align-items: center;
}

.item-name-cell span {
  font-weight: 600 !important;
  color: rgba(70, 70, 80, 1) !important;
}

/* Quantity Cell */
.quantity-cell {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.quantity-chip {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.15) 0%,
    rgba(57, 137, 201, 0.12) 100%) !important;
  color: #3989C9 !important;
  font-weight: 700 !important;
  font-size: 12px !important;
  border: 1px solid rgba(0, 179, 190, 0.3) !important;
  padding: 4px 12px !important;
  height: 24px !important;
}

/* Empty State Styling (nema normativa) */
.norms-table-wrapper + .v-btn {
  margin-top: 16px !important;
}

/* Error indicator on tabs */
.error-indicator {
  color: #ff5252 !important;
  opacity: 0.85 !important;
  transition: opacity 0.3s ease !important;
}

.dialog-tab:hover .error-indicator,
.v-tab--active .error-indicator {
  opacity: 1 !important;
}

/* Error field styling */
.filter-select.error--text >>> fieldset {
  border-color: #ff5252 !important;
  border-width: 2px !important;
}

.filter-select.error--text >>> label {
  color: #ff5252 !important;
}

/* Help icon clickable cursor */
.help-icon-clickable {
  cursor: pointer !important;
  transition: all 0.2s ease !important;
}

.help-icon-clickable:hover {
  transform: scale(1.2);
  opacity: 0.8;
}

/* Help Modal Styling */
.help-modal-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  overflow: hidden;
}

.help-modal-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.9) 50%,
    rgba(97, 84, 164, 0.95) 100%) !important;
  padding: 16px 20px !important;
  color: white !important;
}

.help-modal-title {
  font-size: 20px !important;
  font-weight: 700 !important;
  color: white !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.5px !important;
}

.help-modal-close {
  background: rgba(255, 255, 255, 0.2) !important;
}

.help-modal-close:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  transform: rotate(90deg);
  transition: all 0.3s ease;
}

.help-modal-content {
  background: rgba(250, 250, 250, 0.5) !important;
}

.help-section {
  text-align: center;
}

.help-icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.help-subtitle {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #3989C9 !important;
  letter-spacing: 0.3px;
}

.help-text {
  font-size: 14px !important;
  line-height: 1.6 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  margin: 0;
}

.help-modal-actions {
  background: rgba(250, 250, 250, 0.5);
  border-top: 1px solid rgba(0, 179, 190, 0.15);
}

.help-modal-btn {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.9) 50%,
    rgba(97, 84, 164, 0.95) 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 179, 190, 0.3) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 15px !important;
  letter-spacing: 0.3px !important;
  padding: 0 32px !important;
  transition: all 0.3s ease !important;
}

.help-modal-btn:hover {
  box-shadow: 0 6px 16px rgba(0, 179, 190, 0.4) !important;
  transform: translateY(-2px) scale(1.02);
}
</style>
